/**
 * FilterDialogFooter Component
 * 
 * Footer section with action buttons:
 * - CREATE MY SPARK (primary action) - generates AI activity
 * - RESET PREFERENCES (secondary action) or CANCEL during generation
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HOME_V2_COLORS } from '../colors';
import { useFilter } from '../../../contexts/FilterContext';
import { api } from '../../../services/api';
import type { GenerateActivityFilters } from '../../../types/activity';
import { getAgeRange } from './FilterDialog';

interface FilterDialogFooterProps {
  onClose: () => void;
}

// Progressive loading messages
const LOADING_MESSAGES = [
  'Submitting options...',
  'Customizing prompt...',
  'Generating your activity...',
];

export default function FilterDialogFooter({ onClose }: FilterDialogFooterProps) {
  const { filters, resetFilters } = useFilter();
  const navigate = useNavigate();
  
  // State management
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // AbortController for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  const messageIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Handle activity generation
   */
  const handleCreateSpark = async () => {
    // Reset error state
    setErrorMessage(null);
    setIsGenerating(true);
    setLoadingMessageIndex(0);

    // Create AbortController for cancellation
    abortControllerRef.current = new AbortController();

    // Start progressive message rotation (every 2 seconds)
    messageIntervalRef.current = setInterval(() => {
      setLoadingMessageIndex((prev) => {
        const nextIndex = prev + 1;
        // Stop at last message
        return nextIndex < LOADING_MESSAGES.length ? nextIndex : prev;
      });
    }, 2000);

    try {
      // Derive bounding-box {min, max} from selected age range options
      const ageRangeData = getAgeRange(filters.ageRanges);

      // Prepare filter data
      const filterData: GenerateActivityFilters = {
        location: filters.location || undefined,
        timeMinutes: filters.timeMinutes || undefined,
        kidsCount: filters.kidsCount || undefined,
        ageRange: ageRangeData,
      };

      // Call generation API with abort signal
      const result = await api.generateActivity(
        filterData,
        abortControllerRef.current.signal
      );

      // Success - navigate to generated activity
      navigate(`/activity/${result.activityId}`);
      onClose(); // Close dialog
    } catch (error: unknown) {
      console.error('Generation failed:', error);

      // Handle different error types
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate activity. Please try again.';
      if (errorMessage === 'Activity generation cancelled') {
        // User cancelled - just close dialog
        console.log('Generation cancelled by user');
        onClose();
      } else {
        // Show error message to user
        setErrorMessage(errorMessage);
      }
    } finally {
      // Cleanup
      setIsGenerating(false);
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
      abortControllerRef.current = null;
    }
  };

  /**
   * Handle cancellation during generation
   */
  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear message interval
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
    
    setIsGenerating(false);
    setErrorMessage(null);
  };

  /**
   * Handle reset preferences
   */
  const handleReset = () => {
    resetFilters();
    setErrorMessage(null);
  };

  return (
    <div className="px-6 py-4 border-t border-gray-100">
      {/* Error Message */}
      {errorMessage && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            border: '1px solid #FCA5A5',
          }}
        >
          {errorMessage}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {/* Primary Action - CREATE MY SPARK */}
        <button
          type="button"
          onClick={handleCreateSpark}
          disabled={isGenerating}
          className="w-full px-4 py-3 rounded-lg uppercase transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            backgroundColor: HOME_V2_COLORS.navActive,
            color: '#FFFFFF',
            fontSize: '12px',
            fontWeight: 900,
            letterSpacing: '0.72px',
            lineHeight: 'normal',
            '--tw-ring-color': HOME_V2_COLORS.navActive,
          } as React.CSSProperties}
        >
          {isGenerating ? (
            <span className="flex items-center justify-center gap-2">
              {/* Loading spinner */}
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>{LOADING_MESSAGES[loadingMessageIndex]}</span>
            </span>
          ) : (
            'Create My Spark'
          )}
        </button>

        {/* Secondary Action - RESET PREFERENCES or CANCEL */}
        {isGenerating ? (
          <button
            type="button"
            onClick={handleCancel}
            className="w-full px-4 py-3 rounded-lg uppercase transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: '#FFFFFF',
              color: '#DC2626',
              border: '2px solid #DC2626',
              fontSize: '12px',
              fontWeight: 900,
              letterSpacing: '0.72px',
              lineHeight: 'normal',
              '--tw-ring-color': '#DC2626',
            } as React.CSSProperties}
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            onClick={handleReset}
            className="w-full px-4 py-3 rounded-lg uppercase transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: '#FFFFFF',
              color: HOME_V2_COLORS.navActive,
              border: `2px solid ${HOME_V2_COLORS.navActive}`,
              fontSize: '12px',
              fontWeight: 900,
              letterSpacing: '0.72px',
              lineHeight: 'normal',
              '--tw-ring-color': HOME_V2_COLORS.navActive,
            } as React.CSSProperties}
          >
            Reset Preferences
          </button>
        )}
      </div>
    </div>
  );
}
